import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tune } from "@mui/icons-material";
import { divisionMappings, divisionMappingsSimple, roles } from "../../Constants";
import { LookingForPlayers } from "../../models/LookingForPlayers";
import { TeamTier } from "../../models/TeamTier";
import { isTeamTierTeam } from "../../services/TeamTierService";
import styles from './LookingForPlayersPage2.module.css';

interface FiltersProps {
    posts: LookingForPlayers[];
    setFilteredPosts: (posts: LookingForPlayers[]) => void;
    teamTiers: TeamTier[];
}

// Min. ELO buckets from the redesign: shows posts requiring at least the chosen ELO.
const ELO_BUCKETS = ['all', '2000+', '2500+', '3000+'] as const;
type EloBucket = typeof ELO_BUCKETS[number];
const ELO_THRESHOLD: Record<EloBucket, number> = { 'all': 0, '2000+': 2000, '2500+': 2500, '3000+': 3000 };

// "2 Division" → "2. Division" for chip labels; "Ligaen" unchanged.
const formatDivision = (name: string): string => name.replace(/^(\d) /, '$1. ');

const Filters: React.FC<FiltersProps> = ({ posts, setFilteredPosts, teamTiers }) => {
    const { t } = useTranslation('pages');
    const [eloBucket, setEloBucket] = useState<EloBucket>('all');
    const [selectedDivisions, setSelectedDivisions] = useState<string[]>([]);
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

    const getDivisionPriority = useCallback((division: string): number => {
        // Lower number = higher priority (Ligaen is top)
        const priorities: Record<string, number> = {
            'ligaen': 0,
            '1div': 1,
            '2divA': 2, '2divB': 2,
            '3divA': 3, '3divB': 3, '3divC': 3, '3divD': 3,
            '4divA': 4, '4divB': 4, '4divC': 4, '4divD': 4,
            '5divA': 5, '5divB': 5,
        };
        return priorities[division] ?? 99;
    }, []);

    const applyFilters = useCallback(() => {
        const filteredPosts = posts.filter(post => {
            // Filter on the team's own division — that's what the card displays
            const teamDivision = divisionMappings.get(post.lastDivision)?.split(" ").slice(0, 2).join(" ") || '';
            return post.minELO >= ELO_THRESHOLD[eloBucket] &&
                (selectedDivisions.length === 0 || selectedDivisions.includes(teamDivision)) &&
                (selectedRoles.length === 0 || selectedRoles.some(role => post.roleRequirements.includes(role)));
        });
        filteredPosts.sort((a, b) => {
            // Team donator tier buys top placement, ahead of division priority. Mirrors the
            // donation boost the LFT list already applies to individual free agents.
            const tierA = isTeamTierTeam(a.teamId, teamTiers) ? 0 : 1;
            const tierB = isTeamTierTeam(b.teamId, teamTiers) ? 0 : 1;
            if (tierA !== tierB) return tierA - tierB;
            return getDivisionPriority(a.lastDivision) - getDivisionPriority(b.lastDivision);
        });
        setFilteredPosts(filteredPosts);
    }, [eloBucket, posts, selectedDivisions, selectedRoles, setFilteredPosts, getDivisionPriority, teamTiers]);

    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    const toggleDivision = (division: string) => {
        setSelectedDivisions(prev =>
            prev.includes(division) ? prev.filter(d => d !== division) : [...prev, division]
        );
    };

    const toggleRole = (role: string) => {
        setSelectedRoles(prev =>
            prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
        );
    };

    const resetFilters = () => {
        setEloBucket('all');
        setSelectedDivisions([]);
        setSelectedRoles([]);
    };

    return (
        <div className={styles.filterPanel}>
            <div className={styles.filterHeader}>
                <h2 className={styles.filterTitle}>
                    <Tune className={styles.filterTitleIcon} sx={{ fontSize: 19 }} />
                    {t('lfp.filters')}
                </h2>
                <button className={styles.resetButton} onClick={resetFilters}>
                    {t('lfp.reset')}
                </button>
            </div>

            <div className={styles.filterLabel}>{t('lfp.minEloFilter')}</div>
            <div className={styles.chipRow}>
                {ELO_BUCKETS.map((bucket) => (
                    <button
                        key={bucket}
                        className={`${styles.chip} ${eloBucket === bucket ? styles.chipActive : ''}`}
                        onClick={() => setEloBucket(bucket)}
                    >
                        {bucket === 'all' ? t('lfp.all') : bucket}
                    </button>
                ))}
            </div>

            <div className={styles.filterLabel}>{t('lfp.division')}</div>
            <div className={styles.chipRow}>
                {Array.from(divisionMappingsSimple.values()).map((division) => (
                    <button
                        key={division}
                        className={`${styles.chip} ${selectedDivisions.includes(division) ? styles.chipActive : ''}`}
                        onClick={() => toggleDivision(division)}
                    >
                        {formatDivision(division)}
                    </button>
                ))}
            </div>

            <div className={styles.filterLabel}>{t('lfp.roleWanted')}</div>
            <div className={styles.chipRow}>
                {roles.map((role) => (
                    <button
                        key={role}
                        className={`${styles.chip} ${selectedRoles.includes(role) ? styles.chipActive : ''}`}
                        onClick={() => toggleRole(role)}
                    >
                        {role}
                    </button>
                ))}
            </div>
        </div>
    );
}

export default Filters;
